import  React, {useEffect} from "react";
import clsx from "clsx";
import { App, Button } from "antd";
import { StepIndicator } from "@/components/ui/StepIndicator";
import { DialogMessageType } from "@/types/commands";
import Ring from "@/components/ui/Ring";
import { logger } from "@/helpers/tslog";
import { Step } from "../components/Step";
import SelectPrecedentStep from "./steps/SelectPrecedentStep";
import TimePeriodBenchmarkStep from "./steps/TimePeriodBenchmarkStep";
import FormattingSelectionStep from "./steps/FormattingSelectionStep";
import ImpliedColorsPanel from "../../components/ImpliedColorsPanel";
import { useContentSlideStore, type SlideFrameKey } from "@/state/contentSlideStore";
import { useSlidesDialogStore, type SlideFrameId } from "@/state/slidesDialogStore";
import {
  ensureSlidesOneDriveReady,
  sendSlidesTeamsMessage,
  triggerSlidesGeneration,
} from "../utils/slidesGenerateFlow";
import type { OpenContentReadySlidesMessage } from "@/utils/slides/slidesTaskpaneContract";

const CONTENT_SLIDE_STEP_LABELS = [
  "Select Slide Precedent",
  "Time Period and Relative Benchmark(s)",
  "Formatting",
];

/** Step 0: 2 segments (company → precedent selection) for the step bar fill. */
const QUESTIONS_PER_STEP = [2, 1, 1];
const STEP_SIZES = [1, 1.35, 0.85]; 

function slideFrameKeyToId(key: SlideFrameKey): SlideFrameId {
  const n = Number(key.replace(/^slideFrame/, ""));
  if (!Number.isInteger(n) || n < 1 || n > 9) {
    throw new Error(`Unexpected slide frame key: ${key}`);
  }
  return n as SlideFrameId;
}

export default function ContentSlide() {
  const { message } = App.useApp();
  const [currentStep, setCurrentStep] = React.useState(0);
  const [answersByStep, setAnswersByStep] = React.useState<number[]>([0, 0, 0]);
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [isAuthenticatingOneDrive, setIsAuthenticatingOneDrive] = React.useState(false);
  const primaryCompany = useContentSlideStore((s) => s.primaryCompany);
  const selectedSlideFrameKey = useContentSlideStore((s) => s.selectedSlideFrameKey);
  const selectedFormatTemplateId = useContentSlideStore((s) => s.selectedFormatTemplateId);
  const createNewPrecedentIntent = useContentSlideStore((s) => s.createNewPrecedentIntent);
  const openCreateNewPrecedentPanel = useContentSlideStore((s) => s.openCreateNewPrecedentPanel);

  const hasFrameSelection = selectedSlideFrameKey !== null;
  const step0CompanySelected = !!primaryCompany;
  const step0Complete =
    step0CompanySelected && (hasFrameSelection || createNewPrecedentIntent);

  useEffect(() => {
    setAnswersByStep((prev) => {
      const next0 = step0Complete ? 2 : step0CompanySelected ? 1 : 0;
      const next2 = selectedFormatTemplateId ? QUESTIONS_PER_STEP[2] : 0;
      if (prev[0] === next0 && prev[2] === next2) return prev;
      const next = [...prev];
      next[0] = next0;
      next[2] = next2;
      return next;
    });
  }, [step0Complete, step0CompanySelected, selectedFormatTemplateId]);

  const onClose = React.useCallback(() => {
    try {
      Office.context.ui.messageParent(DialogMessageType.CLOSE);
    } catch {}
  }, []);

  const handleFinish = React.useCallback(async (): Promise<void> => {
    if (!selectedSlideFrameKey) {
      message.error("Please select a slide precedent before finishing.");
      return;
    }
    if (!selectedFormatTemplateId) {
      message.error("Please select a format template before finishing.");
      return;
    }
    if (!primaryCompany) {
      message.error("Primary company is missing.");
      return;
    }

    const oneDriveReady = await ensureSlidesOneDriveReady(message, setIsAuthenticatingOneDrive);
    if (!oneDriveReady) return;

    let dialogResult;
    try {
      dialogResult = await useSlidesDialogStore.getState().complete();
    } catch (e) {
      logger.error("ContentSlide Failed to complete slides dialog", e);
      message.error("Could not finalize the Time Period & Benchmark dialog. Please try again.");
      return;
    }

    let slideFrameId: SlideFrameId;
    try {
      slideFrameId = slideFrameKeyToId(selectedSlideFrameKey);
    } catch (e) {
      logger.error("ContentSlide Invalid slide frame key", e);
      message.error("Unexpected slide frame key.");
      return;
    }

    const generateResult = await triggerSlidesGeneration(
      {
        primaryTicker: primaryCompany.companyTicker,
        primaryExchange: primaryCompany.companyExchange,
        primaryCompanyName: primaryCompany.companyName,
        slidesDialogOutput: dialogResult.slidesDialogOutput,
        selectedSlideFrameIds: [slideFrameId],
        formatTemplateId: selectedFormatTemplateId,
      },
      message,
      setIsGenerating
    );
    if (!generateResult) return;

    try {
      const openMsg: OpenContentReadySlidesMessage = {
        type: DialogMessageType.OPEN_CONTENT_READY_SLIDES,
        jobId: generateResult.jobId,
        slideFrameId,
      };
      Office.context.ui.messageParent(JSON.stringify(openMsg));
    } catch (e) {
      logger.error("ContentSlide Failed to message parent to open content-ready slides", e);
      return;
    }

    message.success({
      content: `Slides generation started successfully. Job ID: ${generateResult.jobId}`,
      duration: 5,
    });

    const teamsSuccess = await sendSlidesTeamsMessage(
      generateResult.jobId,
      generateResult.estimatedDurationMinute,
      generateResult.friendlyRequestIdLine,
      message
    );
    if (!teamsSuccess) return;
    
    if (process.env.API_URL) {
      message.success({ content: "Teams bot was notified successfully.", duration: 4 });
    }

    message.success({ content: "Workflow completed.", duration: 2 });
    onClose();
  }, [
    message,
    onClose,
    primaryCompany,
    selectedFormatTemplateId,
    selectedSlideFrameKey,
  ]);

  const goNext = React.useCallback(() => {
    if (currentStep === 0 && !step0Complete) return;

    if (currentStep === 0 && createNewPrecedentIntent && selectedSlideFrameKey === null) {
      openCreateNewPrecedentPanel();
      return;
    }

    if (currentStep >= CONTENT_SLIDE_STEP_LABELS.length - 1) {
      void handleFinish();
      return;
    }
    setAnswersByStep((prev) => {
      const next = [...prev];
      next[currentStep] = QUESTIONS_PER_STEP[currentStep];
      return next;
    });
    setCurrentStep((s) => s + 1);
  }, [currentStep, handleFinish, step0Complete, createNewPrecedentIntent, openCreateNewPrecedentPanel, selectedSlideFrameKey]);

  const goBack = React.useCallback(() => {
    if (currentStep <= 0) return;
    setCurrentStep((s) => s - 1);
  }, [currentStep]);

  const isLastStep = currentStep === CONTENT_SLIDE_STEP_LABELS.length - 1;

  return (
    <div className="flex flex-col justify-between h-full min-h-screen grid-rows-[auto_1fr_auto] gap-x-2 px-4 py-4 bg-gray-100">
      <div
        className={clsx(
          "w-full mx-auto py-4 shadow-md px-4 rounded-sm z-10 border border-gray-200 border-b-[2px] border-b-[#ff9336] bg-white"
        )}
      >
        <StepIndicator
          currentStep={currentStep}
          answersByStep={answersByStep}
          questionsPerStep={QUESTIONS_PER_STEP}
          labels={[...CONTENT_SLIDE_STEP_LABELS]}
          stepSizes={STEP_SIZES}
          labelClassName="text-xs leading-snug"
        />
      </div>

      <Ring className={`grid h-full relative w-full min-h-[200px] mt-4 !border !border-gray-200 rounded-lg ${currentStep === 2 ? "!rounded-none !border-0 !p-0 !bg-transparent" : ""}`}>
        <Step show={currentStep === 0}>
          <SelectPrecedentStep />
        </Step>
        <Step show={currentStep === 1}>
          <TimePeriodBenchmarkStep />
        </Step>
        <Step show={currentStep === 2}>
          <div className="flex h-full min-h-0 w-full gap-4 overflow-hidden p-2">
            <Ring className="min-w-0 flex-[1.2] overflow-hidden !border !border-gray-200">
              <FormattingSelectionStep impliedColors />
            </Ring>
            <Ring className="min-w-[250px] max-w-full shrink-0 flex-[0.8] overflow-hidden !border !border-gray-200 bg-gray-100">
              <ImpliedColorsPanel />
            </Ring>
          </div>
        </Step>
      </Ring>

      <div className={`flex items-center w-full px-2 pl-10 pt-3 justify-end`}>
        <div className="flex gap-2">
          <Button danger onClick={onClose}>
            Cancel
          </Button>
          <Button type="default" onClick={goBack} disabled={currentStep === 0}>
            Back
          </Button>
          <Button
            type="primary"
            loading={isGenerating || isAuthenticatingOneDrive}
            onClick={goNext}
            disabled={
              (currentStep === 0 && !step0Complete) ||
              (currentStep === 2 && !selectedFormatTemplateId)
            }
          >
            {isLastStep ? "Finish" : "Next"}
          </Button>
        </div>
      </div>
    </div>
  );
}

